import { t } from 'i18next';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import Button from '#/components/Button/Button';
import { ButtonVariant } from '#/service/constants';
import { remify } from '#/service/utils';

import PickerItem, { PickerItemProps } from './PickerItem';

const PickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: min(25rem, 100%);
  margin-bottom: 1rem;
`;

const PickerItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${remify(16)};
  align-self: stretch;
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export interface PickerProps {
  /** items to choose from with the picker */
  items: PickerItemProps[];
  /** handler for what should happen after picking an item */
  onContinue: (id: string) => void;
  /** id of a pre-selected item, this item will be shown as selected at initial render */
  selectedItemId?: string;
  /** handler for triggering when user clicks cancel in component*/
  onCancel?: () => void;
}

/** Picker is responsible for selecting one item from given displayed options
 * you need to press continue button to proceed */
const Picker: FC<PickerProps> = ({
  items,
  selectedItemId = '',
  onContinue,
  onCancel,
}) => {
  const [selected, setSelected] = useState(selectedItemId);

  const handleDone = useCallback(() => {
    onContinue(selected);
  }, [onContinue, selected]);

  return (
    <PickerContainer data-testid="picker">
      <PickerItemContainer>
        {items?.map((item) => (
          <PickerItem
            key={item.id}
            {...item}
            isSelected={selected === item.id}
            onSelect={setSelected}
          />
        ))}
      </PickerItemContainer>
      <ActionContainer>
        <Button
          disabled={!selected}
          isFullWidth
          onClick={handleDone}
          variant={ButtonVariant.Primary}
        >
          {t('button.continue')}
        </Button>
        {onCancel && (
          <Button
            isFullWidth
            onClick={onCancel}
            variant={ButtonVariant.Secondary}
          >
            {t('button.cancel')}
          </Button>
        )}
      </ActionContainer>
    </PickerContainer>
  );
};

export default Picker;
